import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Input from '@mui/joy/Input';
import Table from '@mui/joy/Table';
import Button from '@mui/joy/Button';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Checkbox from '@mui/joy/Checkbox';
import Drawer from '@mui/joy/Drawer';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import CircularProgress from '@mui/joy/CircularProgress';
import Textarea from '@mui/joy/Textarea';
import { fetchTrunks, fetchNumbers, createNumbersForTrunk, updatePhoneNumber, deletePhoneNumber } from '../../api/numbering';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;
const MAX_ADD_NUMBERS = 40;

/** Normalize to E.164-like (digits, optional leading +). */
function normalizeNumberToken(s) {
  const t = String(s).trim().replace(/\s/g, '');
  if (!t) return null;
  const digits = t.replace(/\D/g, '');
  if (digits.length === 0) return null;
  return t.startsWith('+') || t.startsWith('00') ? `+${digits}` : digits;
}

/** Parse "start-end" range (both sides full numbers). Returns array of normalized numbers or null if invalid. */
function parseRange(token) {
  const idx = token.indexOf('-');
  if (idx <= 0 || idx === token.length - 1) return null;
  const start = normalizeNumberToken(token.slice(0, idx));
  const end = normalizeNumberToken(token.slice(idx + 1));
  if (!start || !end) return null;
  const startNum = start.replace(/\D/g, '');
  const endNum = end.replace(/\D/g, '');
  if (startNum.length !== endNum.length) return null;
  const startVal = parseInt(startNum, 10);
  const endVal = parseInt(endNum, 10);
  if (!Number.isFinite(startVal) || !Number.isFinite(endVal) || startVal > endVal) return null;
  const prefix = start.startsWith('+') ? '+' : '';
  const len = startNum.length;
  const out = [];
  for (let n = startVal; n <= endVal; n++) {
    out.push(prefix + String(n).padStart(len, '0'));
  }
  return out;
}

/**
 * Parse user input into deduplicated list of normalized numbers.
 * Input: comma/whitespace separated; tokens can be single number or "start-end" range.
 * @returns {{ valid: string[], invalid: string[] }}
 */
export function parseNumberInput(input) {
  const raw = String(input ?? '')
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const validSet = new Set();
  const invalid = [];
  for (const token of raw) {
    if (token.includes('-')) {
      const expanded = parseRange(token);
      if (expanded) expanded.forEach((n) => validSet.add(n));
      else invalid.push(token);
    } else {
      const n = normalizeNumberToken(token);
      if (n && n.length >= 10) validSet.add(n);
      else invalid.push(token);
    }
  }
  return { valid: [...validSet], invalid };
}

function formatDate(iso) {
  if (!iso) return '–';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

export default function NumberingPanel({ setError, setSelectedAgentId, setActiveTab }) {
  const [trunks, setTrunks] = useState([]);
  const [loadingTrunks, setLoadingTrunks] = useState(false);
  const [trunkError, setTrunkError] = useState(null);
  const [trunkSearch, setTrunkSearch] = useState('');

  const [numbers, setNumbers] = useState([]);
  const [nextOffset, setNextOffset] = useState(null);
  const [loadingNumbers, setLoadingNumbers] = useState(false);
  const [numbersError, setNumbersError] = useState(null);

  const [filterSearch, setFilterSearch] = useState('');
  const [filterSearchDebounced, setFilterSearchDebounced] = useState('');
  const [filterHandler, setFilterHandler] = useState('');
  const [selectedTrunkIds, setSelectedTrunkIds] = useState(() => new Set());

  const [addTrunk, setAddTrunk] = useState(null);
  const [addInput, setAddInput] = useState('');
  const [addOutbound, setAddOutbound] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addResult, setAddResult] = useState(null);

  const [selectedNumberIds, setSelectedNumberIds] = useState(() => new Set());

  const [editOpen, setEditOpen] = useState(false);
  const [editOutbound, setEditOutbound] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setFilterSearchDebounced(filterSearch), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [filterSearch]);

  // Handler options: only handlers that appear in at least one trunk
  const handlerOptions = useMemo(() => {
    const set = new Set();
    trunks.forEach((t) => {
      if (t.handler && String(t.handler).trim()) set.add(t.handler);
    });
    return [...set].sort();
  }, [trunks]);

  const trunkMap = useMemo(() => {
    const m = new Map();
    trunks.forEach((t) => m.set(t.id, t));
    return m;
  }, [trunks]);

  const filteredTrunks = useMemo(() => {
    const q = String(trunkSearch).trim().toLowerCase();
    if (!q) return trunks;
    return trunks.filter(
      (t) =>
        (t.name && t.name.toLowerCase().includes(q)) ||
        (t.id && t.id.toLowerCase().includes(q))
    );
  }, [trunks, trunkSearch]);

  const loadTrunks = useCallback(async () => {
    setLoadingTrunks(true);
    setTrunkError(null);
    try {
      const list = await fetchTrunks();
      setTrunks(list);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to load trunks';
      setTrunkError(msg);
      setError?.(msg);
    } finally {
      setLoadingTrunks(false);
    }
  }, [setError]);

  const selectedTrunkIdsArray = useMemo(() => [...selectedTrunkIds], [selectedTrunkIds]);

  const loadNumbers = useCallback(
    async (offset = 0, append = false) => {
      setLoadingNumbers(true);
      setNumbersError(null);
      try {
        const params = {
          offset,
          pageSize: PAGE_SIZE,
          search: filterSearchDebounced || undefined,
          handler: filterHandler || undefined,
          trunkIds: selectedTrunkIdsArray.length ? selectedTrunkIdsArray : undefined
        };
        const { items, nextOffset: next } = await fetchNumbers(params);
        setNumbers((prev) => (append ? [...prev, ...items] : items));
        setNextOffset(next);
      } catch (err) {
        const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to load numbers';
        setNumbersError(msg);
        setError?.(msg);
        if (!append) setNumbers([]);
      } finally {
        setLoadingNumbers(false);
      }
    },
    [filterSearchDebounced, filterHandler, selectedTrunkIdsArray, setError]
  );

  const toggleTrunkSelection = useCallback((trunkId) => {
    setSelectedTrunkIds((prev) => {
      const next = new Set(prev);
      if (next.has(trunkId)) next.delete(trunkId);
      else next.add(trunkId);
      return next;
    });
  }, []);

  const clearTrunkSelection = useCallback(() => {
    setSelectedTrunkIds(new Set());
  }, []);

  useEffect(() => {
    loadTrunks();
  }, [loadTrunks]);

  useEffect(() => {
    loadNumbers(0, false);
  }, [loadNumbers]);

  const parsedAdd = useMemo(() => parseNumberInput(addInput), [addInput]);

  useEffect(() => {
    if (addTrunk) setAddOutbound(!!addTrunk.outbound);
  }, [addTrunk]);

  const handleAddSubmit = useCallback(async () => {
    if (!addTrunk || parsedAdd.valid.length === 0) return;
    setAddSubmitting(true);
    setAddResult(null);
    try {
      const { created, errors } = await createNumbersForTrunk(addTrunk.id, parsedAdd.valid, addOutbound);
      setAddResult({ created, errors });
      setAddInput('');
      loadNumbers(0, false);
      if (created.length > 0) {
        setAddTrunk(null);
        setAddResult(null);
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to add numbers';
      setAddResult({ created: [], errors: [{ number: '', error: msg }] });
    } finally {
      setAddSubmitting(false);
    }
  }, [addTrunk, parsedAdd.valid, addOutbound, loadNumbers]);

  const closeAddDrawer = useCallback(() => {
    setAddTrunk(null);
    setAddInput('');
    setAddResult(null);
  }, []);

  const selectedNumbersForEdit = useMemo(
    () => numbers.filter((n) => selectedNumberIds.has(n.number)),
    [numbers, selectedNumberIds]
  );
  const selectedCount = selectedNumberIds.size;

  const toggleNumberSelection = useCallback((numberId) => {
    setSelectedNumberIds((prev) => {
      const next = new Set(prev);
      if (next.has(numberId)) next.delete(numberId);
      else next.add(numberId);
      return next;
    });
  }, []);

  const selectAllOnPage = useCallback(() => {
    setSelectedNumberIds((prev) => {
      const next = new Set(prev);
      const onPage = numbers.map((r) => r.number);
      const allSelected = onPage.length > 0 && onPage.every((id) => next.has(id));
      if (allSelected) onPage.forEach((id) => next.delete(id));
      else onPage.forEach((id) => next.add(id));
      return next;
    });
  }, [numbers]);

  const clearNumberSelection = useCallback(() => {
    setSelectedNumberIds(new Set());
  }, []);

  const openEditModal = useCallback(() => {
    const sel = selectedNumbersForEdit;
    setEditOutbound(sel.length > 0 ? !!sel[0].outbound : false);
    setEditError(null);
    setEditOpen(true);
  }, [selectedNumbersForEdit]);

  const handleEditSave = useCallback(async () => {
    if (selectedNumbersForEdit.length === 0) return;
    setEditSaving(true);
    setEditError(null);
    try {
      for (const row of selectedNumbersForEdit) {
        const identifier = row.number?.startsWith('+') ? row.number : `+${row.number}`;
        await updatePhoneNumber(identifier, { outbound: editOutbound });
      }
      setEditOpen(false);
      setSelectedNumberIds(new Set());
      loadNumbers(0, false);
    } catch (err) {
      setEditError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to update');
    } finally {
      setEditSaving(false);
    }
  }, [selectedNumbersForEdit, editOutbound, loadNumbers]);

  const closeEditModal = useCallback(() => {
    setEditOpen(false);
    setEditError(null);
  }, []);

  const openDeleteModal = useCallback(() => {
    setDeleteConfirmInput('');
    setDeleteError(null);
    setDeleteOpen(true);
  }, []);

  const deleteConfirmRangeString = useMemo(() => {
    if (selectedNumbersForEdit.length === 0) return null;
    const digits = (n) => String(n).replace(/\D/g, '');
    if (selectedNumbersForEdit.length === 1) {
      return digits(selectedNumbersForEdit[0].number);
    }
    const sorted = [...selectedNumbersForEdit].sort((a, b) => {
      const da = digits(a.number);
      const db = digits(b.number);
      return da.localeCompare(db, undefined, { numeric: true });
    });
    return `${digits(sorted[0].number)}-${digits(sorted[sorted.length - 1].number)}`;
  }, [selectedNumbersForEdit]);

  const deleteConfirmValid = useMemo(() => {
    if (selectedNumbersForEdit.length === 0 || !deleteConfirmRangeString) return false;
    const input = String(deleteConfirmInput).trim().replace(/\s/g, '');
    const normalized = (s) => String(s).replace(/\D/g, '');
    if (selectedNumbersForEdit.length === 1) {
      return normalized(input) === normalized(deleteConfirmRangeString);
    }
    return input === deleteConfirmRangeString;
  }, [selectedNumbersForEdit, deleteConfirmInput, deleteConfirmRangeString]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirmValid || selectedNumbersForEdit.length === 0) return;
    const toDelete = [...selectedNumbersForEdit];
    setDeleteSaving(true);
    setDeleteError(null);
    try {
      for (let i = 0; i < toDelete.length; i++) {
        const row = toDelete[i];
        const identifier = row.number?.startsWith('+') ? row.number : `+${row.number}`;
        await deletePhoneNumber(identifier);
      }
      setDeleteOpen(false);
      setSelectedNumberIds(new Set());
      setDeleteConfirmInput('');
      loadNumbers(0, false);
    } catch (err) {
      setDeleteError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to delete');
    } finally {
      setDeleteSaving(false);
    }
  }, [deleteConfirmValid, selectedNumbersForEdit, loadNumbers]);

  const closeDeleteModal = useCallback(() => {
    setDeleteOpen(false);
    setDeleteConfirmInput('');
    setDeleteError(null);
  }, []);

  const hasMoreNumbers = nextOffset != null;

  return (
    <Box sx={{ py: 2 }}>
      <Typography level="h4" sx={{ display: { xs: 'none', sm: 'block' } }}>
        Trunks and Numbering
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateRows: '320px minmax(0, 1fr)',
          gridTemplateColumns: '1fr',
          gap: 2,
          mt: 2,
          height: 'calc(100vh - 220px)',
          minHeight: 360,
        }}
      >
        <Box
          sx={{
            minHeight: 0,
            maxHeight: '320px',
            overflow: 'hidden',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
            gap: 2,
          }}
        >
          {/* Left: filters */}
          <Sheet
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 'sm',
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>
                Number search
              </Typography>
              <Input
                size="sm"
                placeholder="Search numbers"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
              />
            </Box>
            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>
                Handler
              </Typography>
              <Select
                size="sm"
                placeholder="All handlers"
                value={filterHandler || null}
                onChange={(_, v) => setFilterHandler(v ?? '')}
                sx={{ width: '100%' }}
              >
                <Option value="">All handlers</Option>
                {handlerOptions.map((h) => (
                  <Option key={h} value={h}>{h}</Option>
                ))}
              </Select>
            </Box>
            <Typography level="body2" color="neutral">
              Select trunks in the right panel to filter numbers.
            </Typography>
          </Sheet>

          {/* Right: trunks list */}
          <Sheet
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 'sm',
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography level="title-sm" sx={{ flexGrow: 1 }}>
                Trunks
              </Typography>
              {selectedTrunkIds.size > 0 && (
                <Button size="sm" variant="plain" color="neutral" onClick={clearTrunkSelection}>
                  Unselect all
                </Button>
              )}
              <Input
                size="sm"
                placeholder="Search trunks"
                value={trunkSearch}
                onChange={(e) => setTrunkSearch(e.target.value)}
                sx={{ maxWidth: 240 }}
              />
            </Box>
            {trunkError && (
              <Typography level="body2" color="danger" sx={{ mb: 1 }}>{trunkError}</Typography>
            )}
            {loadingTrunks && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size="sm" /></Box>}
            {!loadingTrunks && (
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <Table size="sm" stickyHeader sx={{ '--TableCell-paddingX': '0.75rem' }}>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }} aria-label="Select" />
                      <th>Trunk</th>
                      <th>Handler</th>
                      <th>Outbound</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTrunks.length === 0 && (
                      <tr>
                        <td colSpan={5}>
                          <Typography level="body2" color="neutral">No trunks</Typography>
                        </td>
                      </tr>
                    )}
                    {filteredTrunks.map((t) => (
                      <tr
                        key={t.id}
                        onClick={(e) => {
                          if (e.target.closest('button') || e.target.closest('input')) return;
                          toggleTrunkSelection(t.id);
                        }}
                        style={{
                          cursor: 'pointer',
                          background: selectedTrunkIds.has(t.id) ? 'var(--joy-palette-primary-softBg)' : undefined
                        }}
                      >
                        <td>
                          <Checkbox
                            size="sm"
                            checked={selectedTrunkIds.has(t.id)}
                            onChange={() => toggleTrunkSelection(t.id)}
                            aria-label={`Select ${t.name || t.id}`}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td>{t.name || t.id}</td>
                        <td>{t.handler || '–'}</td>
                        <td>{t.outbound ? 'Yes' : 'No'}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAddTrunk(t);
                            }}
                          >
                            Add number
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Box>
            )}
          </Sheet>
        </Box>

        {/* Numbers table */}
        <Sheet
          variant="outlined"
          sx={{
            borderRadius: 'sm',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, pb: 0, flexShrink: 0, flexWrap: 'wrap' }}>
            <Typography level="title-sm" sx={{ flexGrow: 1 }}>
              Numbers
            </Typography>
            <Button
              size="sm"
              variant="outlined"
              disabled={selectedCount === 0}
              onClick={openEditModal}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="outlined"
              color="danger"
              disabled={selectedCount === 0}
              onClick={openDeleteModal}
            >
              Delete
            </Button>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              disabled={selectedCount === 0}
              onClick={clearNumberSelection}
            >
              Clear selection
            </Button>
          </Box>
          {numbersError && (
            <Typography level="body2" color="danger" sx={{ px: 2, pt: 1 }}>{numbersError}</Typography>
          )}
          {loadingNumbers && !numbers.length && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size="sm" /></Box>
          )}
          {!loadingNumbers && numbers.length === 0 && (
            <Typography level="body2" color="neutral" sx={{ p: 2 }}>No numbers. Use filters or add numbers to a trunk.</Typography>
          )}
          {numbers.length > 0 && (
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Table stickyHeader size="sm" sx={{ '--TableCell-paddingX': '0.75rem' }}>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <Checkbox
                        size="sm"
                        checked={numbers.length > 0 && numbers.every((r) => selectedNumberIds.has(r.number))}
                        indeterminate={
                          numbers.some((r) => selectedNumberIds.has(r.number)) &&
                          !numbers.every((r) => selectedNumberIds.has(r.number))
                        }
                        onChange={selectAllOnPage}
                        aria-label="Select all on page"
                      />
                    </th>
                    <th>Number</th>
                    <th style={{ width: '6rem', textAlign: 'center' }}>Handler</th>
                    <th style={{ width: '7rem' }}>Created</th>
                    <th style={{ width: '6rem', textAlign: 'center' }}>Outbound</th>
                    <th style={{ width: '7rem', textAlign: 'center' }}>Provisioned</th>
                    <th style={{ width: '7rem', textAlign: 'center' }}>Call received</th>
                    <th>Trunk</th>
                    <th>Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {numbers.map((row) => (
                    <tr
                      key={row.number}
                      style={{
                        background: selectedNumberIds.has(row.number) ? 'var(--joy-palette-primary-softBg)' : undefined
                      }}
                    >
                      <td>
                        <Checkbox
                          size="sm"
                          checked={selectedNumberIds.has(row.number)}
                          onChange={() => toggleNumberSelection(row.number)}
                          aria-label={`Select ${row.number}`}
                        />
                      </td>
                      <td>{row.number.startsWith('+') ? row.number : `+${row.number}`}</td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{row.handler || '–'}</td>
                      <td title={formatDateTime(row.createdAt)}>{formatDate(row.createdAt)}</td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{row.outbound ? 'Yes' : 'No'}</td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{row.provisioned ? 'Yes' : 'No'}</td>
                      <td
                        style={{ textAlign: 'center', whiteSpace: 'nowrap' }}
                        title={row.callReceived ? formatDateTime(row.callReceived) : ''}
                      >
                        {row.callReceived ? 'Yes' : 'No'}
                      </td>
                      <td>{(row.trunkId && trunkMap.get(row.trunkId)?.name) || row.trunkId || '–'}</td>
                      <td style={{ textAlign: 'left' }}>
                        {row.agentName && row.agentId ? (
                          <Typography
                            level="body-sm"
                            color="primary"
                            sx={{ cursor: 'pointer' }}
                            onClick={() => {
                              setSelectedAgentId?.(row.agentId);
                              setActiveTab?.(2);
                            }}
                          >
                            {row.agentName}
                          </Typography>
                        ) : (
                          'None'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {hasMoreNumbers && (
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    size="sm"
                    variant="outlined"
                    loading={loadingNumbers}
                    onClick={() => loadNumbers(nextOffset, true)}
                  >
                    Load more
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Sheet>
      </Box>

      {/* Edit numbers modal (multi-select) */}
      <Modal open={editOpen} onClose={closeEditModal}>
        <ModalDialog sx={{ maxWidth: 440, width: '100%' }}>
          <ModalClose onClick={closeEditModal} />
          <Typography level="h6">Edit numbers</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography level="body2" color="neutral">
              {selectedNumbersForEdit.length === 1
                ? selectedNumbersForEdit[0].number?.startsWith('+')
                  ? selectedNumbersForEdit[0].number
                  : `+${selectedNumbersForEdit[0].number}`
                : `${selectedNumbersForEdit.length} numbers selected`}
            </Typography>
            {selectedNumbersForEdit.length > 1 && selectedNumbersForEdit.length <= 10 && (
              <Box sx={{ maxHeight: 120, overflow: 'auto', bgcolor: 'background.level1', borderRadius: 'sm', p: 1 }}>
                {selectedNumbersForEdit.map((r) => (
                  <Typography key={r.number} level="body3" component="div">
                    {r.number.startsWith('+') ? r.number : `+${r.number}`}
                  </Typography>
                ))}
              </Box>
            )}
            {selectedNumbersForEdit.length > 10 && (
              <Typography level="body3" color="neutral">
                First 10: {selectedNumbersForEdit.slice(0, 10).map((r) => (r.number.startsWith('+') ? r.number : `+${r.number}`)).join(', ')}…
              </Typography>
            )}
            <Box>
              <Checkbox
                size="sm"
                checked={editOutbound}
                onChange={(e) => setEditOutbound(e.target.checked)}
                label="Outbound capable"
              />
            </Box>
            {editError && (
              <Typography level="body2" color="danger">{editError}</Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={closeEditModal}>Cancel</Button>
              <Button variant="solid" loading={editSaving} onClick={handleEditSave}>Update</Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal open={deleteOpen} onClose={closeDeleteModal}>
        <ModalDialog sx={{ maxWidth: 400, width: '100%' }}>
          <ModalClose onClick={closeDeleteModal} />
          <Typography level="h6" sx={{ fontWeight: 'bold', color: 'danger.plainColor' }}>Delete numbers</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {selectedNumbersForEdit.length === 1 ? (
              <>
                <Typography level="body2" color="neutral">
                  To confirm, enter the number to delete:
                </Typography>
                <Typography level="body2" sx={{ fontFamily: 'monospace' }}>
                  {deleteConfirmRangeString ?? (selectedNumbersForEdit[0].number.startsWith('+') ? selectedNumbersForEdit[0].number : `+${selectedNumbersForEdit[0].number}`)}
                </Typography>
              </>
            ) : (
              <>
                <Typography level="body2" color="neutral">
                  You are about to delete {selectedNumbersForEdit.length} numbers. Enter the exact range to confirm:
                </Typography>
                <Typography level="body2" sx={{ fontFamily: 'monospace', fontWeight: 'lg' }}>
                  {deleteConfirmRangeString}
                </Typography>
              </>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Input
                size="sm"
                placeholder={selectedNumbersForEdit.length === 1 ? 'Enter the number' : 'e.g. 4412340000-4412340099'}
                value={deleteConfirmInput}
                onChange={(e) => setDeleteConfirmInput(e.target.value)}
                color="danger"
                variant="outlined"
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1.5 }}>
                <Button variant="outlined" color="neutral" onClick={closeDeleteModal}>Cancel</Button>
                <Button
                  variant="solid"
                  color="danger"
                  disabled={!deleteConfirmValid}
                  loading={deleteSaving}
                  onClick={handleDeleteConfirm}
                >
                  Delete
                </Button>
              </Box>
            </Box>
            {deleteError && (
              <Typography level="body2" color="danger">{deleteError}</Typography>
            )}
          </Box>
        </ModalDialog>
      </Modal>

      {/* Add-number drawer */}
      <Drawer
        open={!!addTrunk}
        onClose={closeAddDrawer}
        anchor="right"
        size="sm"
        sx={{ '--Drawer-horizontalSize': 'min(400px, 100vw - 32px)' }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography level="h6">Add numbers</Typography>
            <ModalClose onClick={closeAddDrawer} />
          </Box>
          {addTrunk && (
            <>
              <Typography level="body2" color="neutral">
                Trunk: {addTrunk.name || addTrunk.id} {addTrunk.outbound ? '(outbound)' : ''}
              </Typography>
              <Box>
                <Checkbox
                  size="sm"
                  checked={addOutbound}
                  onChange={(e) => setAddOutbound(e.target.checked)}
                  disabled={!addTrunk.outbound}
                  label="Outbound capable"
                />
                {!addTrunk.outbound && (
                  <Typography level="body3" color="neutral" sx={{ ml: 3.5 }}>Trunk does not support outbound</Typography>
                )}
              </Box>
              <Typography level="title-sm">Numbers (one per line, or comma/space separated; use start-end for ranges)</Typography>
              <Textarea
                minRows={4}
                placeholder={'+441234567890\n+4412340000-+4412340099'}
                value={addInput}
                onChange={(e) => setAddInput(e.target.value)}
              />
              {parsedAdd.invalid.length > 0 && (
                <Typography level="body2" color="danger">
                  Invalid: {parsedAdd.invalid.slice(0, 5).join(', ')}{parsedAdd.invalid.length > 5 ? '…' : ''}
                </Typography>
              )}
              {parsedAdd.valid.length > 0 && (
                <Typography level="body2" color={parsedAdd.valid.length > MAX_ADD_NUMBERS ? 'danger' : 'neutral'}>
                  {parsedAdd.valid.length} number(s) to add
                  {parsedAdd.valid.length > MAX_ADD_NUMBERS && ` — maximum ${MAX_ADD_NUMBERS} allowed`}
                </Typography>
              )}
              {addResult && (
                <Box>
                  {addResult.created.length > 0 && (
                    <Typography level="body2" color="success">Created: {addResult.created.length}</Typography>
                  )}
                  {addResult.errors.length > 0 && (
                    <Typography level="body2" color="danger">
                      Errors: {addResult.errors.map((e) => e.number || e.error).join(', ')}
                    </Typography>
                  )}
                </Box>
              )}
              <Box sx={{ mt: 'auto', display: 'flex', gap: 1 }}>
                <Button
                  variant="solid"
                  disabled={parsedAdd.valid.length === 0 || parsedAdd.valid.length > MAX_ADD_NUMBERS || addSubmitting}
                  loading={addSubmitting}
                  onClick={handleAddSubmit}
                >
                  Add numbers
                </Button>
                <Button variant="outlined" onClick={closeAddDrawer}>Cancel</Button>
              </Box>
            </>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}
